import React, {useEffect, useState} from "react";
import {Box, Button, IconButton, Stack, Typography} from "@mui/material";
import Toolbar from "@mui/material/Toolbar";
import {
    RegistrationApiGet, useDeleteRegistrationImageMutation, useGetRegImagesByIdQuery,
    useGetRegistrationByIdQuery,
    usePostInterruptLineMutation, usePostRegistrationImageMutation,
    usePutInterruptLineMutation,
    usePutRegistrationByIdMutation,
} from "./reg_api";
import {Route, Routes, useHref, useNavigate, useParams} from "react-router-dom";
import Scan from "./Scan";
import {useDispatch, useSelector} from "react-redux";
import {current, resetReg} from "./reg_reducer";
import BookmarkAdd from "@mui/icons-material/BookmarkAdd";
import Construction from "@mui/icons-material/Construction";
import Delete from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import Pause from "@mui/icons-material/Pause";
import PersonSearch from "@mui/icons-material/PersonSearch";
import PlayArrow from "@mui/icons-material/PlayArrow";
import RegStatus from "./RegStatus";
import Title from "../navigation/Title";
import {RootState} from "../../store";
import {CurrentUserInfo, Role} from "../../session_state";
import InitializeApp from "../../components/InitializeApp";
import {FetchBaseQueryError} from "@reduxjs/toolkit/query";
import SnackError from "../../components/SnackError";
import OpSelectWrapper from "./OpSelectWrapper";
import {IntrListController} from "../interruption/Interruptions";
import RegEdit from "./RegEdit";
import {WorkOrderListController} from "../workOrders/WorkOrders";
import {useHasRole} from "../../hooks/useRole";
import RegWork from "./RegWork";
import RegWorker from "./RegWorker";
import RegOper from "./RegOper";
import DeleteReg from "./DeleteReg";
import {MachineListController} from "../machines/Machines";
import RegMachine from "./RegMachine";
import RegFinish from "./RegFinish";
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import {newError} from "../errors/errors";
import {UnregisteredListController} from "../people/UnregisteredWorkers";
import Upload from "@mui/icons-material/Upload";
import ImageUploadDialog from "../storage/ImageUploadDialog";
import {supabase} from "../../supabase";
import {Tables} from "../../apitypes";
import RegImage from "./RegImage";
import {useGetWorkOrderProductsCountQuery} from "../workOrders/workorder_api";

type MainVirewProps = {
    reg: RegistrationApiGet
}


const MainView = ({reg}: MainVirewProps) => {
    const hideLabels = {display: {"xs": "none", "sm": "block"}}

    const canDelete = useHasRole([Role.Admin, Role.Foreman])
    const navigate = useNavigate();
    const [uploading, setUploading] = useState(false)

    const {data: images, error: imgError, isFetching} = useGetRegImagesByIdQuery({id: reg.id})
    const {data: productCount, error: countError} = useGetWorkOrderProductsCountQuery({id: reg.WorkOrderID})
    const [put, {error}] = usePutRegistrationByIdMutation();
    const [postImg, {error: postImgErr}] = usePostRegistrationImageMutation();
    const [delImg, {error: delImgErr}] = useDeleteRegistrationImageMutation();
    const [putIntr, {error: errIntr, isLoading}] = usePutInterruptLineMutation();

    const onUpload = (path: string) => {
        postImg({RegistrationID: reg.id, image_url: path})
        setUploading(false)
    }

    const onDelete = async (removeImage: Tables<"reg_images">) => {
        const {data, error} = await supabase.storage
            .from("registrations")
            .remove([removeImage.image_url])
        console.log(data, error)
        delImg({id: removeImage.id, RegistrationID: reg.id})
    }

    const isNotFinished = reg.Status != "RegFinished"
    const isNotHalted = reg.Status !== "RegHalted"
    const isNotFinishable =
        reg.WorkerID == null ||
        reg.OperationID == null ||
        (reg.workTask?.NeedsMachine && reg.MachineID === null) ||
        (reg.workOrder == null && reg.workTask?.NeedsWorkOrder === true) ||
        (productCount && productCount.count > 0 && reg.ProductID === null)


    return <Box>
        <SnackError error={error}/>
        <SnackError error={imgError}/>
        <SnackError error={postImgErr}/>
        <SnackError error={delImgErr}/>
        <Title title={"Registracija"}/>
        <Toolbar>
            <Button
                disabled={!isNotFinished}
                title={"Poišči  operacijo"}
                onClick={() => navigate("./operation")}
            >
                <Construction fontSize={"large"}/><Box component={"span"} sx={hideLabels}>Operacija</Box>
            </Button>
            <Button
                disabled={!isNotFinished}
                title={"Poišči in dodaj delo"}
                onClick={() => navigate("./work")}>
                <BookmarkAdd fontSize={"large"}/><Box component={"span"} sx={hideLabels}>Delo</Box>
            </Button>

            <Button
                disabled={!isNotFinished}
                title={"Poišči in dodaj delavca"}
                onClick={() => navigate("./person")}>
                <PersonSearch fontSize={"large"}/><Box component={"span"} sx={hideLabels}>Delavec</Box>
            </Button>
            <Button
                disabled={!isNotFinished}
                title={"Poišči in dodaj stroj"}
                onClick={() => navigate("./machine")}>
                <PrecisionManufacturingIcon fontSize={"large"}/><Box component={"span"} sx={hideLabels}>Stroj</Box>
            </Button>
            {reg.Status != "RegFinished" && <Scan id={reg.id}/>}
        </Toolbar>

        <Box sx={{
            display: "grid",
            gridGap: {xs: "0.5em", sm: "1em"},
            gridTemplateColumns: {xs: "1fr", sm: "1fr 2fr 1fr", md: "1fr 1fr 1fr"}
        }}>
            <RegStatus reg={reg} flex={false}/>
            <Stack sx={{gap: "1em"}}>
                <RegOper reg={reg}/>
                <RegWork reg={reg} prodCount={productCount?.count || 0}/>
            </Stack>
            <Stack sx={{gap: "1em"}}>
                <RegWorker reg={reg}/>
                <RegMachine reg={reg}/>
            </Stack>
        </Box>
        <Box sx={{mt: 1, mb: 3, display: "flex", flexGap: "0.5em"}}>
            {isNotFinished && isNotHalted && <Button
                sx={{mr: 1}}
                href={`./finish`}
                color={"success"}
                variant={"contained"}
                disabled={isNotFinishable}
                title={"Zaključi registracijo"}
                size={"large"}>Zaključi</Button>}
            {isNotHalted && isNotFinished && <Button
                variant={"contained"}
                onClick={() => {
                    navigate("./intr")
                }}
                color={"warning"}
                sx={{mr: 1}}
                title={"Prijavi prekinitev"}
                size={"large"}>
                <Pause/><Box component={"span"} sx={{display: {xs: "none", sm: "block"}}}>Prekinitev</Box>
            </Button>}
            {!isNotHalted && isNotFinished && <Button
                variant={"contained"}
                disabled={isLoading}
                onClick={async () => {
                    const intr = reg.interruptLine.find(intr => intr.EndTime === null)
                    if (intr) {
                        await putIntr({id: intr.id, body: {"EndTime": new Date().toISOString()}})
                    }
                    if (reg.Status == "RegHalted") {
                        await put({id: reg.id, body: {Status: "RegInProgress"}})
                    }
                }}
                color={"success"}
                sx={{mr: 1}}
                title={"Končaj prekinitev"}
                size={"large"}>
                <PlayArrow/><Box component={"span"} sx={{display: {xs: "none", sm: "block"}}}>Končaj prekinitev</Box>
            </Button>}
            {(isNotFinished || canDelete) && <Button
                onClick={() => {
                    navigate("./delete")
                }}
                variant={"contained"}
                color={"secondary"}
                sx={{mr: 1}}
                size={"large"}>
                <Delete/><Box component={"span"} sx={{display: {xs: "none", sm: "block"}}}>Briši</Box>
            </Button>}
            {(!isNotFinished || canDelete) && <Button
                onClick={() => {
                    navigate("./edit")
                }}
                variant={"contained"}
                color={"secondary"}
                sx={{mr: 1}}
                size={"large"}>
                <Edit/><Box component={"span"} sx={{display: {xs: "none", sm: "block"}}}>Uredi</Box>
            </Button>}
            {isNotFinished && <Button variant={"contained"} sx={{color: "white"}} onClick={() => setUploading(true)}>
                <Upload fontSize={"small"}/> Naloži slike
            </Button>}
        </Box>
        <Box sx={{display: "flex", flexWrap: "wrap", gap: "1em"}}>
            {images && images.result.map((img) => <RegImage regStatus={reg.Status} image={img} onDelete={onDelete}/>)}
        </Box>
        <ImageUploadDialog
            uploading={uploading}
            onUpload={onUpload}
            bucket={"registrations"}
            onClose={() => setUploading(false)}
        />
    </Box>
}


export default () => {
    const {id} = useParams<"id">()
    const {data, refetch, error, isFetching} = useGetRegistrationByIdQuery({
        id: parseInt(id || "")
    });
    const dispatch = useDispatch();
    const [put, {error: err}] = usePutRegistrationByIdMutation()
    const [createIntr, {error: intrErr}] = usePostInterruptLineMutation()

    useEffect(() => {
        if (err) {
            dispatch(newError(err))
        }
    }, [err]);
    useEffect(() => {
        if (intrErr) {
            dispatch(newError(intrErr))
        }
    }, [intrErr]);

    const navigate = useNavigate();

    useEffect(() => {
        dispatch(current(data))
        dispatch(resetReg())
    }, [data])

    const [seq, setSeq] = useState(0)
    useEffect(() => {
        if (data && data.Status === "RegHalted") {
            const tm = setTimeout(() => {
                refetch()
                setSeq(seq + 1)
            }, 15000)
            return () => clearTimeout(tm)
        }
        return
    }, [data, seq])
    const cu = useSelector<RootState, CurrentUserInfo | null>(s => s.sessionState.user);
    const defWorker = cu?.defaultWorkerID || null;

    if (isFetching && !data) {
        return <InitializeApp/>;
    }

    if (error) {
        return <Box sx={{mt: "30vh", mr: "auto", ml: "auto", textAlign: "center"}}>
            <Typography variant={"subtitle1"} color={"secondary"}>{(error as FetchBaseQueryError).status}</Typography>
            <Typography variant={"h2"} color={"secondary"}>Napaka pri nalaganju registracije!</Typography>
            <SnackError error={error}/>
        </Box>
    }

    if (!data) {
        return <Box sx={{mt: "1em"}}>
            <Typography variant={"h2"} sx={{color: "error.main"}}>Registracija {id} ne obstaja...</Typography>
        </Box>
    }

    const workerId = data.WorkerID || defWorker || null;

    return data && <>
        <Routes>
            <Route
                path={"/"}
                element={<MainView reg={data}/>}
            />
            <Route
                path={"/person"}
                element={<UnregisteredListController
                    onSelect={async (id) => {
                        await put({id: data.id, body: {WorkerID: id}})
                        navigate(`/reg/${data.id}`, {replace: true});
                    }}/>
                }
            />
            <Route
                path={"/operation"}
                element={<OpSelectWrapper workerID={workerId} onSelect={async (id) => {
                    await put({
                        id: data.id, body: {
                            OperationID: id
                        }
                    })
                    navigate(`/reg/${data.id}`, {replace: true});
                }}/>}
            />
            <Route
                path={"/machine"}
                element={<MachineListController selectable={true}
                                                onSelect={async (id: number, operationID?: number | null) => {
                                                    await put({
                                                        id: data.id,
                                                        body: {
                                                            MachineID: id,
                                                            OperationID: operationID ?? data.OperationID
                                                        }
                                                    })
                                                    navigate(`/reg/${data.id}`, {replace: true});
                                                }}/>}
            />
            <Route
                path={"/finish"}
                element={<RegFinish onCancel={() => navigate(`/reg/${data.id}`, {replace: true})} reg={data}/>}
            />
            <Route
                path={"/work"}
                element={<WorkOrderListController
                    pageSize={20}
                    selectable={true}
                    onSelect={async (id) => {
                        await put({id: data.id, body: {WorkOrderID: id, ProductID: null}})
                        navigate(`/reg/${data.id}`, {replace: true});
                    }}
                />}
            />
            <Route
                path={"/intr"}
                element={<IntrListController selectable={true} pageSize={20} onSelect={async (id) => {
                    const res = await createIntr({
                        RegistrationID: data.id,
                        InterruptID: id,
                        StartTime: new Date().toISOString(),
                    })
                    await put({id: data.id, body: {Status: "RegHalted"}})
                    if ("data" in res) {
                        navigate(`/reg/${data.id}`, {replace: true});
                    }
                }}/>}
            />
            <Route
                path={"/delete"}
                element={<DeleteReg onCancel={() => navigate(`/reg`, {replace: true})} reg={data}/>}
            />
            <Route
                path={"/edit"}
                element={<RegEdit onCancel={() => navigate(`/reg/${data.id}`, {replace: true})} reg={data}/>}
            />

        </Routes>
    </>

}

